import { useCallback, useEffect, useState } from 'react';
import {
  fetchActiveBaselineRevision,
  type BaselineRevisionSummary,
} from '../api/baselineApis';

export interface UseActiveBaselineRevisionResult {
  activeRevision: BaselineRevisionSummary | null;
  loading: boolean;
  refresh: () => Promise<BaselineRevisionSummary | null>;
}

/**
 * Loads GET baseline/revision/active once per project (page load / project change).
 * Call `refresh` after create/submit/approve/reject/discard/refresh-snapshot or schedule invalidate.
 * 404 → null (no open revision).
 */
export function useActiveBaselineRevision(
  projectId?: string | null,
): UseActiveBaselineRevisionResult {
  const [activeRevision, setActiveRevision] = useState<BaselineRevisionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setActiveRevision(null);
      setLoading(false);
      return null;
    }
    try {
      const revision = await fetchActiveBaselineRevision({ projectId }, { silent: true });
      setActiveRevision(revision);
      return revision;
    } catch {
      // Keep last known value on transient errors (404 already returns null).
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [projectId, refresh]);

  return { activeRevision, loading, refresh };
}
